import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import truncateEthAddress from "truncate-eth-address";
import { useTranslation } from "react-i18next";

const Breadcrumb = () => {
    const location = useLocation();
    const { t } = useTranslation();

    const generateBreadcrumbs = () => {
        const paths = location.pathname.split("/").filter((path) => path);
        const breadcrumbs = [
            {
                label: <img src="/logo512.png" alt="BidCoin" className="w-6 h-auto pt-[2px]" />,
                path: "/",
            },
        ];

        let currentPath = "";
        paths.forEach((path) => {
            currentPath += `/${path}`;

            // Handle special cases
            if (path === "auction-details") {
                breadcrumbs.push({ label: t("breadcrumb.auction"), path: "/auctions" });
                return;
            }
            if (path === "news") {
                breadcrumbs.push({ label: t("breadcrumb.news"), path: currentPath });
                return;
            }
            // Check if we're in a news article path
            if (paths[0] === "news" && path !== "news") {
                const articleTitle = location.state?.title || t("breadcrumb.article");
                breadcrumbs.push({ label: articleTitle, path: currentPath });
                return;
            }
            if (path.match(/^0x[a-fA-F0-9]{40}$/)) {
                // This is an address - show truncated version
                const truncated = truncateEthAddress(path);
                breadcrumbs.push({ label: truncated.substring(6, 12), path: currentPath });
                return;
            }

            switch (path.toLowerCase()) {
                case "profile":
                    breadcrumbs.push({ label: t("breadcrumb.profile"), path: currentPath });
                    break;
                case "achievements":
                    breadcrumbs.push({ label: t("breadcrumb.achievements"), path: currentPath });
                    break;
                case "settings":
                    breadcrumbs.push({ label: t("breadcrumb.settings"), path: currentPath });
                    break;
                case "overview":
                    breadcrumbs.push({ label: t("breadcrumb.overview"), path: currentPath });
                    break;
                case "how-bidcoin-auctions-work":
                    breadcrumbs.push({ label: "How It Works", path: currentPath });
                    break;
                default:
                    // Capitalize each word in hyphenated strings
                    const label = path
                        .split("-")
                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(" ");
                    breadcrumbs.push({ label, path: currentPath });
            }
        });
        return breadcrumbs;
    };

    const breadcrumbs = generateBreadcrumbs();
    if (breadcrumbs.length === 1 || breadcrumbs.length === 0)
        return (
            <div
                className="flex items-center space-x-1 cursor-pointer py-2"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
                <img src="/logo512.png" alt="Logo" className="h-11 w-11 sm:h-14 sm:w-14" />
                <span className="text-amber-500 font-medium pl-1 text-sm sm:text-base">
                    BIDCOIN
                </span>
            </div>
        );
    return (
        <nav className="flex items-center space-x-1 text-sm text-zinc-400 ">
            {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.path}>
                    {index > 0 && <ChevronRight className="w-3 h-3" />}
                    {index === breadcrumbs.length - 1 ? (
                        <span
                            className={`text-zinc-100 h-max-10 max-w-24 text-[1rem]   ${
                                breadcrumbs.length > 3 && breadcrumbs[2].path === "/cw/news"
                                    ? "line-clamp-2"
                                    : "whitespace-nowrap"
                            }`}
                        >
                            {crumb.label}
                        </span>
                    ) : (
                        <Link
                            to={crumb.path}
                            className="hover:text-zinc-100 transition-colors whitespace-nowrap line-clamp-1 text-[1rem] "
                        >
                            {crumb.label}
                        </Link>
                    )}
                </React.Fragment>
            ))}
        </nav>
    );
};

export default Breadcrumb;
